import React, { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { useAtomValue, useSetAtom } from "jotai/react";

import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { ExpensesCard } from "@/blocks/ExpensesCard";
import { VnBox } from "@/components/VnBox";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { hasExpensesConfig } from "@/utils/householdMember";

export const ApplicationHouseholdExpensesScreen = () => {
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const { data: householdMembers = [] } = useListHouseholdMembersQuery();
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const handleToggleExpand = (memberId: string) => {
    setExpandedMemberId(prev => (prev === memberId ? null : memberId));
  };

  const handleClose = () => {
    setExpandedMemberId(null);
  };

  const { totalMembersCount, completedMembersCount } = useMemo(() => {
    const total = householdMembers.length;
    const completed = householdMembers.filter(member =>
      hasExpensesConfig(member),
    ).length;
    return { totalMembersCount: total, completedMembersCount: completed };
  }, [householdMembers]);

  const handleNext = () => {
    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "HOUSEHOLD_EXPENSES",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          router.navigate({
            pathname: "/(authed)/(modal)/application/documents",
          });
        },
      },
    );
  };

  return (
    <ApplicationScreenLayout
      onNextButtonPress={handleNext}
      titleI18nKey="native:applicationExpensesScreen.title"
      descriptionI18nKey="native:applicationExpensesScreen.description">
      <VnBox className="gap-4">
        {householdMembers.map(member => {
          const isExpanded = expandedMemberId === member.id;
          return (
            <ExpensesCard
              key={member.id}
              member={member}
              isExpanded={isExpanded}
              onToggleExpand={() => handleToggleExpand(member.id)}
              onClose={handleClose}
            />
          );
        })}
        <VnText
          variant="lg"
          className="text-muted-foreground text-end"
          i18nKey="common:progressIndicator"
          i18nOptions={{
            progress: completedMembersCount,
            total: totalMembersCount,
          }}
        />
      </VnBox>
    </ApplicationScreenLayout>
  );
};
